/**
 * Reading and writing the amount a cashier types, with strings and integers only.
 *
 * What the cashier types is converted to integer minor units exactly (no floating point, no rounding), so
 * the backend receives exactly what was entered. Text that cannot be read exactly is refused with a reason
 * and never "fixed". This is input parsing, not money arithmetic: no amount is added, subtracted or compared.
 */
import { currencyFractionDigits } from '@/lib/money';

export type AmountProblem = 'empty' | 'format' | 'precision' | 'zero' | 'too_large';

export type ParsedAmount = { ok: true; minor: number } | { ok: false; problem: AmountProblem };

const MAX_DIGITS = 12;

/** "500", "500.5" and "500.50" all read as 50050 in a two-digit currency. "500.555", "1,000" and "abc" do not read. */
export function parseAmountInput(text: string, currency: string): ParsedAmount {
  const value = text.trim();
  if (value === '') return { ok: false, problem: 'empty' };
  const match = /^(\d+)(?:\.(\d*))?$/.exec(value);
  if (!match) return { ok: false, problem: 'format' };
  const digits = currencyFractionDigits(currency);
  const whole = match[1];
  const fraction = match[2] ?? '';
  if (fraction.length > digits) return { ok: false, problem: 'precision' };
  const joined = (whole + fraction.padEnd(digits, '0')).replace(/^0+(?=\d)/, '');
  if (joined.length > MAX_DIGITS) return { ok: false, problem: 'too_large' };
  const minor = Number(joined);
  if (!Number.isSafeInteger(minor)) return { ok: false, problem: 'too_large' };
  if (minor === 0) return { ok: false, problem: 'zero' };
  return { ok: true, minor };
}

/** "Enter an amount" style sentence for a problem, for the field's error line. */
export function describeAmountProblem(problem: AmountProblem, currency: string): string {
  const digits = currencyFractionDigits(currency);
  switch (problem) {
    case 'empty':
      return 'Enter an amount.';
    case 'zero':
      return 'Enter an amount greater than zero.';
    case 'precision':
      return digits === 0
        ? 'Enter a whole amount, without decimals.'
        : `Use at most ${digits} decimal places.`;
    case 'too_large':
      return 'That amount is too large.';
    default:
      return 'Enter the amount as digits, for example 500.00.';
  }
}

/** An integer minor-unit amount written as a plain decimal string for an input ("85000" → "850.00"). Presentation only. */
export function minorToInput(minor: number, currency: string): string {
  const digits = currencyFractionDigits(currency);
  const text = String(Math.max(0, Math.trunc(minor)));
  if (digits === 0) return text;
  const padded = text.padStart(digits + 1, '0');
  return `${padded.slice(0, -digits)}.${padded.slice(-digits)}`;
}
